/**
 * PerformanceCalculationService - Service für Finanz-Performance-Metriken
 *
 * Dieser Service implementiert professionelle, wissenschaftlich fundierte
 * Performance-Analyse-Methoden für Monte-Carlo-Simulationen.
 *
 * Kernfunktionen:
 * - Statistische Performance-Zusammenfassung für Monte-Carlo-Ergebnisse
 * - Empirischer Maximum Drawdown mit Jahres-Granularität
 * - Sortino Ratio für Downside-Risikoanalyse
 *
 * Von MonteCarloTab.tsx extrahiert um Business Logic von UI zu trennen.
 * Optimiert für große Monte-Carlo-Simulationen mit bis zu 10.000 Iterationen.
 *
 * ARCHITECTURE: Pure service class - NO React/UI dependencies
 * UI rendering is handled by components that consume this service's data.
 *
 * @author Portfolio-3Topf Team
 * @version 2.2.0 (tote Funktionen entfernt)
 * @since 1.8.0
 */

import {
  MetricDescriptions,
  PerformanceMetricDisplay,
} from '../types/performance';
import { SIMULATION_CONFIG, SimulationUtils } from '../config/simulation';
import { StatisticalHelpers } from '@/utils/statisticalHelpers';

/**
 * Ergebnis einer kombinierten Monte-Carlo-Iteration (vermeidet zirkuläre Imports)
 *
 * Diese Schnittstelle definiert die Struktur für Vergleichsanalysen zwischen
 * 1-Topf und 3-Topf Strategien innerhalb einer Monte-Carlo-Iteration.
 */
interface CombinedMonteCarloResult {
  /** Iterationsnummer für Tracking und Debugging */
  iteration: number;

  /** Ergebnisse der 3-Topf-Strategie */
  threeTopf: {
    /** Verbleibendes Kapital am Ende der Simulation (in Euro) */
    finalBalance: number;

    /** Summe aller Entnahmen über die Simulationsdauer (in Euro) */
    totalWithdrawals: number;

    /** true wenn Portfolio die gesamte Entnahmephase überlebt hat */
    successfulStrategy: boolean;
    
    /** Jahr-für-Jahr Daten - SINGLE SOURCE OF TRUTH für alle Berechnungen */
    jahresergebnisse: any[];
  };

  /** Ergebnisse der 1-Topf-Strategie (identische Struktur) */
  oneTopf: {
    finalBalance: number;
    totalWithdrawals: number;
    successfulStrategy: boolean;
    
    /** Jahr-für-Jahr Daten - SINGLE SOURCE OF TRUTH für alle Berechnungen */
    jahresergebnisse: any[];
  };
}

/** Typ-Definition für Portfolio-Strategien in Monte-Carlo-Vergleichen */
type StrategyType = 'threeTopf' | 'oneTopf';

// Types now imported from ../types/performance.ts

/**
 * PerformanceCalculationService - Hauptklasse für professionelle Portfolio-Performance-Analyse
 *
 * Diese Klasse implementiert wissenschaftlich fundierte Performance-Metriken nach
 * internationalen Standards der Finanzindustrie (CFA Institute, GIPS-Standards).
 */
export class PerformanceCalculationService {
  // Private helper methods


  private static calculateSortinoRatio(returns: number[], riskFreeRate: number): number {
    // Inline mean calculation to avoid dependency on deleted helper
    const meanReturn = returns.length === 0 ? 0 : returns.reduce((sum, r) => sum + r, 0) / returns.length;

    // Downside-Ziel vereinheitlicht auf riskFreeRate (Standard-Definition der
    // Sortino Ratio: Abweichungen werden gegen die Zielrendite gemessen, nicht
    // gegen 0). Das ist konsistent mit dem Sortino-Block in
    // generatePerformanceSummary, der ebenfalls riskFreeRate als Ziel nutzt.
    const downsideReturns = returns.filter(r => r < riskFreeRate);

    // Ohne Renditen unterhalb der Zielrendite ist das Downside-Risiko nicht
    // messbar: 0 als dokumentierter Sentinel statt Infinity, damit
    // Durchschnitte und Perzentile nicht durch Infinity-Werte verfälscht werden
    if (downsideReturns.length === 0) {return 0;}

    const downsideDeviation = Math.sqrt(
      downsideReturns.reduce((sum, r) => sum + Math.pow(r - riskFreeRate, 2), 0) /
        downsideReturns.length
    );

    if (downsideDeviation === 0) {return 0;}
    // Kein ×12/√12-Faktor: returns sind bereits jährliche Renditen.
    return (meanReturn - riskFreeRate) / downsideDeviation;
  }

  /**
   * Empirischer Maximum Drawdown aus einer Wertreihe.
   * Läuft über die Reihe, führt den bisherigen Peak mit und bestimmt
   * den größten relativen Rückgang vom Peak. Rückgabe als POSITIVER Prozentwert.
   *
   * @param series - Wertreihe (z.B. kapitalIndex oder gesamtsaldo je Jahr)
   * @param anchor - Vor-Renten-Niveau, mit dem der Peak VOR dem ersten Element
   *   verankert wird (z.B. 100 für die kapitalIndex-Reihe, initialBalance für die
   *   gesamtsaldo-Reihe). Ohne Anker würde ein Crash bereits im ersten Element
   *   der Reihe (series[0]) nie als Drawdown erkannt, da der Peak dann direkt
   *   auf dem bereits gefallenen Wert initialisiert würde.
   */
  private static calculateEmpiricalMaxDrawdownPercent(series: number[], anchor: number): number {
    if (series.length === 0) {return 0;}

    let maxDrawdown = 0;
    let peak = anchor;

    for (const value of series) {
      if (value > peak) {peak = value;}
      if (peak > 0) {
        const drawdown = (peak - value) / peak;
        if (drawdown > maxDrawdown) {maxDrawdown = drawdown;}
      }
    }

    return maxDrawdown * 100;
  }

  /**
   * Generiert Performance-Zusammenfassung für UI-Darstellung (Monte-Carlo-Ergebnisse)
   *
   * Diese umfassende Methode erstellt eine detaillierte Performance-Analyse für Monte-Carlo-Simulationen
   * mit wissenschaftlich fundierten Berechnungen und statistischen Auswertungen.
   *
   * Von MonteCarloTab.tsx hierher verschoben um Business Logic von UI zu trennen.
   * Implementiert erweiterte statistische Methoden für professionelle Portfolio-Analyse.
   *
   * @param results - Array von CombinedMonteCarloResult für Strategie-Vergleich
   * @param strategy - Portfolio-Strategie ('threeTopf' oder 'oneTopf')
   * @param initialBalance - Tatsächliches Startkapital der Simulation (z.B. steuerParameter.vorhandenesKapital)
   * @returns Array von PerformanceMetricDisplay für tabellarische UI-Darstellung
   *
   * @example
   * ```typescript
   * const results = combinedMonteCarloEngine.runSimulation(10000);
   * const summary3Topf = PerformanceCalculationService.generatePerformanceSummary(
   *   results,
   *   'threeTopf',
   *   4440000
   * );
   *
   * summary3Topf.forEach(metric => {
   *   console.log(`${metric.metric}: P50=${metric.p50}, P90=${metric.p90}`);
   * });
   * ```
   *
   * @note Maximum Drawdown wird empirisch aus den Jahresdaten berechnet (bevorzugt kapitalIndex)
   * @note Annual Mean Return ist eine vereinfachte geldgewichtete Rendite (Näherung, kein Modified Dietz)
   */
  static generatePerformanceSummary(
    results: CombinedMonteCarloResult[],
    strategy: StrategyType,
    initialBalance: number
  ): PerformanceMetricDisplay[] {
    if (!results || results.length === 0) {return [];}

    const riskFreeRate = SIMULATION_CONFIG.DEFAULT_RISK_FREE_RATE;

    // Use consolidated statistical helpers

    // Extract core data for calculations
    const finalBalances = results.map(r => r[strategy].finalBalance);

    // Calculate time-weighted returns (nominal)
    // Echter Time-Weighted Return aus den Jahresdaten:
    // Entnahme-Timing: Jahresanfangs-Entnahme, d.h. V_t = (V_{t-1} - W_t) * (1 + r_t)
    // => Periodenrendite r_t = V_t / (V_{t-1} - W_t) - 1
    // Geometrische Verkettung und Annualisierung: (∏(1+r_t))^(1/n) - 1
    // periods = tatsächlicher TWR-Horizont dieser Iteration (kann bei Depletion
    // vor Simulationsende < jahresergebnisse.length sein). Wird zusammen mit
    // dem Nominalwert zurückgegeben, damit die reale Deflationierung weiter
    // unten über DENSELBEN Horizont rechnet (Fix 11a).
    const timeWeightedReturnsData = results.map(r => {
      const jahresergebnisse = r[strategy].jahresergebnisse;
      let previousBalance = initialBalance;
      let cumulativeGrowth = 1;
      let periods = 0;

      for (const year of jahresergebnisse) {
        const investedCapital = previousBalance - (year.gesamtentnahme || 0);
        // Guard: bei Depletion ((V_{t-1} - W_t) <= 0) Verkettung beenden
        if (!(investedCapital > 0)) {break;}

        cumulativeGrowth *= year.gesamtsaldo / investedCapital;
        periods++;
        previousBalance = year.gesamtsaldo;

        if (!(previousBalance > 0)) {break;}
      }

      if (periods <= 0 || !(cumulativeGrowth >= 0)) {return { nominalReturn: 0, periods: 0 };}
      return { nominalReturn: (Math.pow(cumulativeGrowth, 1 / periods) - 1) * 100, periods };
    });

    const timeWeightedReturnsNominal = timeWeightedReturnsData.map(d => d.nominalReturn);

    // Kumulierter Inflationsfaktor je Iteration aus dem SIMULIERTEN Inflationspfad
    // (jahresergebnisse[].inflationsrate in Prozent): ∏(1 + i_t/100)
    // Über die VOLLE Simulationsdauer - wird für Portfolio End Balance (real)
    // gebraucht, die den tatsächlichen Endwert am Ende der GESAMTEN Simulation
    // deflationiert (unabhängig vom TWR-Horizont).
    const cumulativeInflationFactors = results.map(r =>
      r[strategy].jahresergebnisse.reduce(
        (factor: number, year: any) => factor * (1 + (year.inflationsrate || 0) / 100),
        1
      )
    );

    // TWR-spezifischer Inflationsfaktor: nur über die ersten `periods` Jahre,
    // also DENSELBEN Horizont wie der nominale TWR. Ohne diesen Abgleich würde
    // bei Depletion vor Simulationsende die (kürzere) TWR-Rendite gegen die
    // Inflation der VOLLEN Simulationsdauer deflationiert (Horizont-Mismatch,
    // Fix 11a).
    const twrCumulativeInflationFactors = timeWeightedReturnsData.map((d, idx) =>
      results[idx][strategy].jahresergebnisse
        .slice(0, d.periods)
        .reduce((factor: number, year: any) => factor * (1 + (year.inflationsrate || 0) / 100), 1)
    );

    // Calculate time-weighted returns (real) - Fisher gegen die annualisierte simulierte Inflation
    const timeWeightedReturnsReal = timeWeightedReturnsData.map((d, idx) => {
      const { nominalReturn, periods } = d;
      // Depletion-Sentinel (periods === 0, siehe oben): real ebenfalls als
      // Sentinel behandeln statt einen 0-Nominalwert fälschlich zu
      // deflationieren - das würde einen nicht existenten "realen" Verlust
      // allein aus der Inflationsrechnung erzeugen (Fix 11a).
      if (periods <= 0) {return 0;}
      const annualizedInflation = Math.pow(twrCumulativeInflationFactors[idx], 1 / periods) - 1;
      // Fisher: real = (1 + nominal) / (1 + Inflation) - 1
      return ((1 + nominalReturn / 100) / (1 + annualizedInflation) - 1) * 100;
    });

    // Calculate portfolio end balances (real) - deflationiert mit dem
    // kumulierten Inflationsfaktor des simulierten Inflationspfads der Iteration
    const portfolioEndBalancesReal = results.map((r, idx) => {
      return r[strategy].finalBalance / cumulativeInflationFactors[idx];
    });

    // Calculate annual returns for each iteration
    // Vereinfachte geldgewichtete Rendite (Näherung):
    // ((Endwert + Gesamtentnahmen) / Startwert)^(1/Jahre) - 1
    // Hinweis: KEIN Modified Dietz - Cashflows werden nicht zeitgewichtet.
    const annualReturns = results.map(r => {
      const finalBalance = r[strategy].finalBalance;
      const totalWithdrawals = r[strategy].totalWithdrawals;
      const years = r[strategy].jahresergebnisse.length;

      if (years <= 0) {return 0;}

      const totalReturn = finalBalance + totalWithdrawals - initialBalance;

      if (initialBalance <= 0) {return 0;}
      const annualReturn =
        (Math.pow((initialBalance + totalReturn) / initialBalance, 1 / years) - 1) * 100;

      return annualReturn;
    });

    // Calculate proper volatility AND Sortino ratio per iteration from the SAME
    // year-by-year return time series. Beide Kennzahlen werden aus derselben
    // yearlyReturns-Zeitreihe abgeleitet, damit sie nicht zweimal berechnet
    // werden muss (vorher: eigene O(n)-Schleife für Volatilität, UND ein
    // O(n²)-Filter auf dem Querschnitt der Endrenditen für Sortino).
    // FIXED v2.4.11: Use actual jahresergebnisse to calculate rolling volatility per iteration
    const perIterationStats = results.map(r => {
      const jahresergebnisse = r[strategy].jahresergebnisse;

      if (jahresergebnisse.length < 2) {
        return { volatility: 0, sortino: 0 }; // Cannot calculate with less than 2 years
      }

      // Calculate year-over-year returns from actual simulation data
      // Entnahme-Timing: Jahresanfangs-Entnahme => r_t = V_t / (V_{t-1} - W_t) - 1
      // Jahr 0 (Index 0) ist ein VOLLES Entnahmejahr und darf nicht übersprungen
      // werden - Basis dafür ist initialBalance (konsistent mit dem TWR-Pfad oben).
      const yearlyReturns: number[] = [];
      let previousBalance = initialBalance;
      for (let i = 0; i < jahresergebnisse.length; i++) {
        const currentYear = jahresergebnisse[i];

        const investedCapital = previousBalance - (currentYear.gesamtentnahme || 0);
        if (previousBalance > 0 && investedCapital > 0) {
          const returnRate = (currentYear.gesamtsaldo / investedCapital - 1) * 100;
          yearlyReturns.push(returnRate);
        }
        previousBalance = currentYear.gesamtsaldo;
      }

      if (yearlyReturns.length < 2) {
        return { volatility: 0, sortino: 0 };
      }

      return {
        volatility: StatisticalHelpers.calculateStandardDeviation(yearlyReturns),
        // Sortino je Iteration aus DERSELBEN Jahresrenditen-Zeitreihe (nicht
        // aus dem Querschnitt der Endrenditen aller Iterationen). Der 0-Sentinel
        // bei fehlendem Downside kommt bereits aus calculateSortinoRatio -
        // kein künstliches 5.0-Cap mehr, das die P10-P90-Verteilung verzerrt.
        sortino: this.calculateSortinoRatio(yearlyReturns, riskFreeRate),
      };
    });

    const volatilities = perIterationStats.map(s => s.volatility);
    const sortinoRatios = perIterationStats.map(s => s.sortino);

    // Calculate proper Sharpe ratios. Epsilon-Guard statt > 0: Gleitkomma-Restrauschen
    // (~1e-13) in deterministischen Szenarien würde sonst Sharpe-Werte von ~1e15 erzeugen.
    const sharpeRatios = annualReturns.map((ret, idx) => {
      const vol = volatilities[idx];
      return vol > 1e-9 ? (ret - riskFreeRate) / vol : 0;
    });

    // EMPIRISCHER Maximum Drawdown aus den vorliegenden Jahresdaten:
    // über die Wertreihe laufen, Peak mitführen, maxDD = max((peak - value)/peak).
    // Bevorzugt die kapitalIndex-Reihe (cashflow-bereinigter Marktindex), da der
    // Gesamtsaldo durch Entnahmen strukturell fällt. Fallback: gesamtsaldo-Reihe
    // (enthält dann auch entnahmebedingte Rückgänge - siehe Metrik-Beschreibung).
    const drawdownData = results.map(r => {
      const jahresergebnisse = r[strategy].jahresergebnisse;
      const hasKapitalIndex =
        jahresergebnisse.length > 0 &&
        jahresergebnisse.every(
          (year: any) => typeof year.kapitalIndex === 'number' && Number.isFinite(year.kapitalIndex)
        );

      const series: number[] = jahresergebnisse.map((year: any) =>
        hasKapitalIndex ? year.kapitalIndex : year.gesamtsaldo
      );

      // Anker VOR dem ersten Jahr: kapitalIndex startet bei 100 (Vor-Renten-Niveau,
      // siehe BaseSimulationEngine.calculateCurrentKapitalIndex), die gesamtsaldo-Reihe
      // beim tatsächlichen Startkapital. So geht ein Crash bereits im ersten
      // Entnahmejahr (Jahr 0) nicht als "kein Drawdown" verloren.
      const anchor = hasKapitalIndex ? 100 : initialBalance;

      return {
        maxDrawdown: this.calculateEmpiricalMaxDrawdownPercent(series, anchor),
        basedOnKapitalIndex: hasKapitalIndex,
      };
    });

    const maxDrawdowns = drawdownData.map(d => d.maxDrawdown);

    // Calculate maximum drawdowns excluding cashflows:
    // Die kapitalIndex-Reihe ist bereits cashflow-frei => Wert direkt übernehmen.
    // Nur beim gesamtsaldo-Fallback wird die heuristische Cashflow-Reduktion angewendet.
    const maxDrawdownsExcludingCashflows = drawdownData.map((d, idx) => {
      if (d.basedOnKapitalIndex) {return d.maxDrawdown;}
      const years = results[idx][strategy].jahresergebnisse.length;
      const cashflowReduction = SimulationUtils.calculateCashflowReduction(years);
      return d.maxDrawdown * (1 - cashflowReduction);
    });

    // Calculate safe withdrawal rates
    const safeWithdrawalRates = results.map(r => {
      const annualWithdrawal = r[strategy].totalWithdrawals / r[strategy].jahresergebnisse.length;
      return (annualWithdrawal / initialBalance) * 100;
    });

    // Calculate perpetual withdrawal rates based on portfolio sustainability
    const perpetualWithdrawalRates = safeWithdrawalRates.map((swr, idx) => {
      const returnRate = annualReturns[idx];
      const volatility = volatilities[idx];
      const sharpe = sharpeRatios[idx];

      // Advanced perpetual rate calculation based on:
      // 1. Return stability (Sharpe ratio)
      // 2. Return level relative to historical market performance
      // 3. Volatility-adjusted safety margin

      // Base conservatism: higher for higher volatility
      const volatilityAdjustment = Math.max(0.6, 1 - volatility / 100);

      // Sharpe adjustment: better risk-adjusted returns allow higher rates
      const sharpeAdjustment = Math.min(1.0, 0.8 + Math.max(0, sharpe) * 0.1);

      // Return sustainability: sustainable returns allow higher perpetual rates
      const returnThreshold = 6; // 6% real return threshold
      const returnAdjustment = Math.min(
        0.95,
        0.7 + Math.max(0, (returnRate - returnThreshold) / 20)
      );

      // Combined perpetual rate: conservative approach
      const perpetualMultiplier = volatilityAdjustment * sharpeAdjustment * returnAdjustment;

      return swr * perpetualMultiplier;
    });

    return [
      {
        metric: 'Time Weighted Rate of Return (nominal)',
        metricWithInfo: 'Time Weighted Rate of Return (nominal)',
        p10: `${StatisticalHelpers.calculatePercentile(timeWeightedReturnsNominal, 10).toFixed(2)}%`,
        p25: `${StatisticalHelpers.calculatePercentile(timeWeightedReturnsNominal, 25).toFixed(2)}%`,
        p50: `${StatisticalHelpers.calculatePercentile(timeWeightedReturnsNominal, 50).toFixed(2)}%`,
        p75: `${StatisticalHelpers.calculatePercentile(timeWeightedReturnsNominal, 75).toFixed(2)}%`,
        p90: `${StatisticalHelpers.calculatePercentile(timeWeightedReturnsNominal, 90).toFixed(2)}%`,
      },
      {
        metric: 'Time Weighted Rate of Return (real)',
        metricWithInfo: 'Time Weighted Rate of Return (real)',
        p10: `${StatisticalHelpers.calculatePercentile(timeWeightedReturnsReal, 10).toFixed(2)}%`,
        p25: `${StatisticalHelpers.calculatePercentile(timeWeightedReturnsReal, 25).toFixed(2)}%`,
        p50: `${StatisticalHelpers.calculatePercentile(timeWeightedReturnsReal, 50).toFixed(2)}%`,
        p75: `${StatisticalHelpers.calculatePercentile(timeWeightedReturnsReal, 75).toFixed(2)}%`,
        p90: `${StatisticalHelpers.calculatePercentile(timeWeightedReturnsReal, 90).toFixed(2)}%`,
      },
      {
        metric: 'Portfolio End Balance (nominal)',
        metricWithInfo: 'Portfolio End Balance (nominal)',
        p10: `${Math.round(StatisticalHelpers.calculatePercentile(finalBalances, 10)).toLocaleString('de-DE')} €`,
        p25: `${Math.round(StatisticalHelpers.calculatePercentile(finalBalances, 25)).toLocaleString('de-DE')} €`,
        p50: `${Math.round(StatisticalHelpers.calculatePercentile(finalBalances, 50)).toLocaleString('de-DE')} €`,
        p75: `${Math.round(StatisticalHelpers.calculatePercentile(finalBalances, 75)).toLocaleString('de-DE')} €`,
        p90: `${Math.round(StatisticalHelpers.calculatePercentile(finalBalances, 90)).toLocaleString('de-DE')} €`,
      },
      {
        metric: 'Portfolio End Balance (real)',
        metricWithInfo: 'Portfolio End Balance (real)',
        p10: `${Math.round(StatisticalHelpers.calculatePercentile(portfolioEndBalancesReal, 10)).toLocaleString('de-DE')} €`,
        p25: `${Math.round(StatisticalHelpers.calculatePercentile(portfolioEndBalancesReal, 25)).toLocaleString('de-DE')} €`,
        p50: `${Math.round(StatisticalHelpers.calculatePercentile(portfolioEndBalancesReal, 50)).toLocaleString('de-DE')} €`,
        p75: `${Math.round(StatisticalHelpers.calculatePercentile(portfolioEndBalancesReal, 75)).toLocaleString('de-DE')} €`,
        p90: `${Math.round(StatisticalHelpers.calculatePercentile(portfolioEndBalancesReal, 90)).toLocaleString('de-DE')} €`,
      },
      {
        metric: 'Annual Mean Return (nominal)',
        metricWithInfo: 'Annual Mean Return (nominal)',
        p10: `${StatisticalHelpers.calculatePercentile(annualReturns, 10).toFixed(2)}%`,
        p25: `${StatisticalHelpers.calculatePercentile(annualReturns, 25).toFixed(2)}%`,
        p50: `${StatisticalHelpers.calculatePercentile(annualReturns, 50).toFixed(2)}%`,
        p75: `${StatisticalHelpers.calculatePercentile(annualReturns, 75).toFixed(2)}%`,
        p90: `${StatisticalHelpers.calculatePercentile(annualReturns, 90).toFixed(2)}%`,
      },
      {
        metric: 'Annualized Volatility',
        metricWithInfo: 'Annualized Volatility',
        p10: `${StatisticalHelpers.calculatePercentile(volatilities, 10).toFixed(2)}%`,
        p25: `${StatisticalHelpers.calculatePercentile(volatilities, 25).toFixed(2)}%`,
        p50: `${StatisticalHelpers.calculatePercentile(volatilities, 50).toFixed(2)}%`,
        p75: `${StatisticalHelpers.calculatePercentile(volatilities, 75).toFixed(2)}%`,
        p90: `${StatisticalHelpers.calculatePercentile(volatilities, 90).toFixed(2)}%`,
      },
      {
        metric: 'Sharpe Ratio',
        metricWithInfo: 'Sharpe Ratio',
        p10: StatisticalHelpers.calculatePercentile(sharpeRatios, 10).toFixed(2),
        p25: StatisticalHelpers.calculatePercentile(sharpeRatios, 25).toFixed(2),
        p50: StatisticalHelpers.calculatePercentile(sharpeRatios, 50).toFixed(2),
        p75: StatisticalHelpers.calculatePercentile(sharpeRatios, 75).toFixed(2),
        p90: StatisticalHelpers.calculatePercentile(sharpeRatios, 90).toFixed(2),
      },
      {
        metric: 'Sortino Ratio',
        metricWithInfo: 'Sortino Ratio',
        p10: StatisticalHelpers.calculatePercentile(sortinoRatios, 10).toFixed(2),
        p25: StatisticalHelpers.calculatePercentile(sortinoRatios, 25).toFixed(2),
        p50: StatisticalHelpers.calculatePercentile(sortinoRatios, 50).toFixed(2),
        p75: StatisticalHelpers.calculatePercentile(sortinoRatios, 75).toFixed(2),
        p90: StatisticalHelpers.calculatePercentile(sortinoRatios, 90).toFixed(2),
      },
      {
        metric: 'Maximum Drawdown',
        metricWithInfo: 'Maximum Drawdown',
        p10: `-${StatisticalHelpers.calculatePercentile(maxDrawdowns, 10).toFixed(2)}%`,
        p25: `-${StatisticalHelpers.calculatePercentile(maxDrawdowns, 25).toFixed(2)}%`,
        p50: `-${StatisticalHelpers.calculatePercentile(maxDrawdowns, 50).toFixed(2)}%`,
        p75: `-${StatisticalHelpers.calculatePercentile(maxDrawdowns, 75).toFixed(2)}%`,
        p90: `-${StatisticalHelpers.calculatePercentile(maxDrawdowns, 90).toFixed(2)}%`,
      },
      {
        metric: 'Maximum Drawdown Excluding Cashflows',
        metricWithInfo: 'Maximum Drawdown Excluding Cashflows',
        p10: `-${StatisticalHelpers.calculatePercentile(maxDrawdownsExcludingCashflows, 10).toFixed(2)}%`,
        p25: `-${StatisticalHelpers.calculatePercentile(maxDrawdownsExcludingCashflows, 25).toFixed(2)}%`,
        p50: `-${StatisticalHelpers.calculatePercentile(maxDrawdownsExcludingCashflows, 50).toFixed(2)}%`,
        p75: `-${StatisticalHelpers.calculatePercentile(maxDrawdownsExcludingCashflows, 75).toFixed(2)}%`,
        p90: `-${StatisticalHelpers.calculatePercentile(maxDrawdownsExcludingCashflows, 90).toFixed(2)}%`,
      },
      {
        metric: 'Safe Withdrawal Rate',
        metricWithInfo: 'Safe Withdrawal Rate',
        p10: `${StatisticalHelpers.calculatePercentile(safeWithdrawalRates, 10).toFixed(2)}%`,
        p25: `${StatisticalHelpers.calculatePercentile(safeWithdrawalRates, 25).toFixed(2)}%`,
        p50: `${StatisticalHelpers.calculatePercentile(safeWithdrawalRates, 50).toFixed(2)}%`,
        p75: `${StatisticalHelpers.calculatePercentile(safeWithdrawalRates, 75).toFixed(2)}%`,
        p90: `${StatisticalHelpers.calculatePercentile(safeWithdrawalRates, 90).toFixed(2)}%`,
      },
      {
        metric: 'Perpetual Withdrawal Rate',
        metricWithInfo: 'Perpetual Withdrawal Rate',
        p10: `${StatisticalHelpers.calculatePercentile(perpetualWithdrawalRates, 10).toFixed(2)}%`,
        p25: `${StatisticalHelpers.calculatePercentile(perpetualWithdrawalRates, 25).toFixed(2)}%`,
        p50: `${StatisticalHelpers.calculatePercentile(perpetualWithdrawalRates, 50).toFixed(2)}%`,
        p75: `${StatisticalHelpers.calculatePercentile(perpetualWithdrawalRates, 75).toFixed(2)}%`,
        p90: `${StatisticalHelpers.calculatePercentile(perpetualWithdrawalRates, 90).toFixed(2)}%`,
      },
    ];
  }

  /**
   * Liefert wissenschaftlich fundierte Beschreibungen für Performance-Metriken
   *
   * Diese Methode stellt detaillierte, fachlich korrekte Erklärungen für alle
   * Performance-Kennzahlen bereit, inklusive mathematischer Formeln und praktischer Interpretation.
   *
   * Von MonteCarloTab.tsx hierher verschoben für bessere Code-Organisation.
   *
   * @param metric - Name der Performance-Metrik (z.B. 'Sharpe Ratio', 'Maximum Drawdown')
   * @returns Detaillierte deutsche Erklärung mit Formeln und Anwendungsbeispielen
   *
   * @example
   * ```typescript
   * const description = PerformanceCalculationService.getMetricDescription('Sharpe Ratio');
   * console.log(description);
   * // Ausgabe: "Risikobereinigte Renditekennzahl nach William F. Sharpe..."
   *
   * const drawdownInfo = PerformanceCalculationService.getMetricDescription('Maximum Drawdown');
   * // Enthält Magdon-Ismail & Atiya Formel und Interpretation
   * ```
   */
  static getMetricDescription(metric: string): string {
    const descriptions: MetricDescriptions = {
      'Time Weighted Rate of Return (nominal)': `Jährliche Rendite des Portfolios ohne Inflationsbereinigung (in %).
        
        Formel: TWRR = (Endwert / Startwert)^(1/Jahre) - 1
        
        Diese Kennzahl zeigt die durchschnittliche jährliche Wertsteigerung des Portfolios in Prozent. Der Time-Weighted Return eliminiert den Einfluss von Cashflows und misst die reine Anlageperformance. Basiert auf den tatsächlichen Simulationsergebnissen aus ${SIMULATION_CONFIG.MONTE_CARLO.DEFAULT_ITERATIONS.toLocaleString('de-DE')} Monte-Carlo-Iterationen.`,

      'Time Weighted Rate of Return (real)': `Inflationsbereinigte jährliche Rendite für reale Kaufkraftentwicklung.

        Formel (Fisher-Gleichung): TWRR (real) = (1 + TWRR (nominal)) / (1 + annualisierte Inflation) - 1
        Annualisierte Inflation = geometrisches Mittel der SIMULIERTEN Inflationsraten über denselben Zeithorizont wie der nominale TWR (nicht die pauschale ${SIMULATION_CONFIG.DEFAULT_AVERAGE_INFLATION_RATE}%-Annahme)

        Zeigt die reale Kaufkraft-Entwicklung des Portfolios über die Zeit. Bei vorzeitiger Kapitalerschöpfung (Depletion) vor Simulationsende gilt ein dokumentierter Sentinel-Wert von 0 statt einer über einen inkonsistenten Zeithorizont verzerrten Deflationierung.`,

      'Portfolio End Balance (nominal)': `Verbleibendes Portfoliovermögen am Ende der Simulation in nominalen Euro-Werten.
        
        Berechnung: Startvermögen + kumulierte Renditen - kumulierte Entnahmen
        
        Dies ist der tatsächliche Euro-Betrag, der nach allen geplanten Entnahmen noch im Portfolio verbleibt. Positive Werte zeigen ein erfolgreiches Portfolio, das die Entnahmephase überlebt hat.`,

      'Portfolio End Balance (real)': `Verbleibendes Portfoliovermögen inflationsbereinigt in heutiger Kaufkraft.
        
        Formel: Real-Endwert = Nominal-Endwert / (1 + Inflation)^Jahre
        
        Berechnet durch Deflationierung des nominalen Endwerts mit dem kumulierten Inflationsfaktor über die Simulationsdauer. Zeigt die tatsächliche Kaufkraft des verbleibenden Vermögens in heutigen Euro-Werten.`,

      'Annual Mean Return (nominal)': `Durchschnittliche jährliche Gesamtrendite inklusive aller Entnahmen.
        
        Formel: Rendite = ((Endwert + Gesamtentnahmen) / Startwert)^(1/Jahre) - 1
        
        Zeigt die Rendite auf das ursprünglich investierte Kapital unter Berücksichtigung sowohl der Portfolioentwicklung als auch der ausgezahlten Entnahmen. Diese Kennzahl reflektiert die Gesamtperformance der Anlagestrategie.`,

      'Annualized Volatility': `Streuung der jährlichen Portfoliorenditen als Risikomaß.

        Formel: σ = √(Σ(Ri - μ)² / (n-1))
        Ri = jährliche Portfoliorenditen (r_t = V_t / (V_(t-1) - W_t) - 1, inkl. Jahr 0), μ = arithmetisches Mittel, n = Anzahl Jahre

        Stichproben-Standardabweichung (Bessel-Korrektur, ÷(n-1)) direkt auf den bereits jährlichen Renditen - kein zusätzlicher ×√12-Hochrechnungsfaktor, da keine monatlichen Renditen zugrunde liegen. Höhere Werte bedeuten größere Unsicherheit. Werte unter ${SIMULATION_CONFIG.METRICS.MODERATE_VOLATILITY_THRESHOLD}% gelten als moderat, über ${SIMULATION_CONFIG.METRICS.HIGH_VOLATILITY_THRESHOLD}% als hoch.`,

      'Sharpe Ratio': `Risikobereinigte Renditekennzahl nach William F. Sharpe.

        Formel: Sharpe = (Portfoliorendite - Risikofreier Zins) / Volatilität
        Portfoliorendite = arithmetisches Mittel der jährlichen Renditen, Risikofreier Zins: ${SIMULATION_CONFIG.DEFAULT_RISK_FREE_RATE}% (aus Konfiguration)

        Ein höherer Wert bedeutet bessere Rendite pro Risikoeinheit. Liegt die Volatilität unter 1e-9 (praktisch schwankungsfreies Szenario, z.B. konstante Rendite ohne Rauschen), wird als dokumentierter Sentinel 0 ausgegeben statt einer Division durch (nahezu) Null. Bewertung: >1,5 = exzellent, >1,0 = gut, >0,5 = akzeptabel, <0,5 = schwach. Werte über ${SIMULATION_CONFIG.METRICS.GOOD_SHARPE_RATIO} gelten als attraktiv für risikobewusste Anleger.`,

      'Sortino Ratio': `Modifizierte Sharpe Ratio, die nur negative Abweichungen (Downside-Risiko) berücksichtigt.

        Formel: Sortino = (Portfoliorendite - Zielrendite) / Downside-Deviation
        Zielrendite = Risikofreier Zins (${SIMULATION_CONFIG.DEFAULT_RISK_FREE_RATE}%); Downside-Deviation = √(Σ(Ri - Ziel)² / m), Summe NUR über die m Jahresrenditen Ri < Zielrendite (nicht über alle n Jahre)

        Fokussiert auf tatsächliche Verlustrisiken und ignoriert positive Volatilität. Gibt es keine Rendite unterhalb der Zielrendite, ist Downside-Risiko nicht messbar - dokumentierter Sentinel 0 statt Infinity, damit Durchschnitte/Perzentile nicht verzerrt werden. Höhere Werte sind besser, da nur das Verlustrisiko bestraft wird. Besonders relevant für Entnahmestrategien.`,

      'Maximum Drawdown': `Empirisch gemessener größter zwischenzeitlicher Wertverlust vom bisherigen Höchststand, als Prozentwert (z.B. -35% = ein Rückgang um 35% vom Höchststand).

        Formel: MDD = max_t[(Peak_t - Wert_t) / Peak_t] × 100%, Peak_t = laufendes Maximum der Wertreihe bis Zeitpunkt t
        Bevorzugt die kapitalIndex-Reihe (cashflow-bereinigter Marktindex, Peak-Anker bei 100 vor dem ersten Jahr), Fallback auf die gesamtsaldo-Reihe (Anker beim Startkapital), falls kein kapitalIndex vorliegt

        Direkte empirische Auswertung der simulierten Wertreihe - keine Brownian-Motion-Schätzung nach Magdon-Ismail & Atiya, kein Withdrawal-Stress-Aufschlag und keine Kappung bei einem festen Maximalwert mehr. Der gesamtsaldo-Fallback enthält auch entnahmebedingte Rückgänge (siehe "Maximum Drawdown Excluding Cashflows" für eine Bereinigung um diesen Effekt).`,

      'Maximum Drawdown Excluding Cashflows': `Größter Portfolioverlust ohne Berücksichtigung der planmäßigen Entnahmen.
        
        Formel: DD (ohne Cashflows) = Maximum Drawdown × (1 - Cashflow-Reduktion)
        Cashflow-Reduktion: ${SIMULATION_CONFIG.CASHFLOW_REDUCTION_BASE * 100}% - ${SIMULATION_CONFIG.CASHFLOW_REDUCTION_MAX * 100}% je nach Simulationsdauer
        
        Zeigt reine Marktrisiken ohne Entnahmeeffekte. Typischerweise 10-25% niedriger als der Gesamt-Drawdown. Wichtig für die Bewertung der zugrundeliegenden Anlagestrategie unabhängig von den Entnahmen.`,

      'Safe Withdrawal Rate': `Nachhaltige jährliche Entnahmerate basierend auf tatsächlichen Simulationsergebnissen.
        
        Formel: SWR = (Jährliche Entnahme / Startvermögen) × 100%
        Jährliche Entnahme = Gesamtentnahmen / Anzahl Jahre
        
        Zeigt die realistisch mögliche Entnahmerate für die jeweilige Strategie und Marktbedingungen. Anders als die theoretische 4%-Regel basiert diese Kennzahl auf den spezifischen Simulationsparametern und Marktannahmen.`,

      'Perpetual Withdrawal Rate': `Wissenschaftlich fundierte Entnahmerate für theoretisch unbegrenzte Portfoliolaufzeit.
        
        Formel: PWR = SWR × Volatilitäts-Adj. × Sharpe-Adj. × Rendite-Adj.
        Volatilitäts-Adj. = max(0,6; 1 - Volatilität/100)
        Sharpe-Adj. = min(1,0; 0,8 + max(0; Sharpe) × 0,1)
        Rendite-Adj. = min(0,95; 0,7 + max(0; (Rendite-6%)/20))
        
        Erweiterte Berechnung basierend auf Renditestabilität (Sharpe Ratio), Volatilitätsrisiko und nachhaltigem Renditeniveau. Berücksichtigt wissenschaftliche Erkenntnisse zur langfristigen Portfoliostabilität. Diese Rate soll theoretisch unbegrenzt aufrechterhalten werden können.`,
    };

    return descriptions[metric] || 'Keine Beschreibung verfügbar.';
  }

}
